import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, Space, message, Card, Row, Col, Empty, Modal, Tooltip, Table } from 'antd';
import { SearchOutlined, SyncOutlined, RobotOutlined, DownloadOutlined, QuestionCircleOutlined, FilePdfOutlined } from '@ant-design/icons';
import { getEntityData, getPicklistData, getAIAnalysis } from '../../utils/apiUtils';
import html2canvas from 'html2canvas';
import html2pdf from 'html2pdf.js';
import './DiagnosisAssistant.css';
import { useNavigate } from 'react-router-dom';

// 添加文件服务器地址常量
const FILE_SERVER_URL = 'http://123.60.160.177:18080/filex/img';

// 将 getMappedValue 函数移到组件外部作为工具函数
const getMappedValue = (value, mapping) => {
  if (!value) return '-';
  if (typeof value === 'object' && value !== null) {
    return value.text || value.toString() || '-';
  }
  return mapping[value]?.text || value || '-';
};

// 将 renderFileLink 函数移到组件外部
const renderFileLink = (fileData) => {
  if (!fileData) return '-';
  
  const handleFileClick = (e) => {
    e.preventDefault();
    
    // 如果是字符串，直接拼接完整URL
    const fileUrl = typeof fileData === 'string' 
      ? `${FILE_SERVER_URL}/${fileData}`
      : fileData.url || `${FILE_SERVER_URL}/${fileData}`;

    if (!fileUrl) {
      message.error('文件链接无效');
      return;
    }

    // 打开新窗口预览
    window.open(fileUrl, '_blank');
  };

  return (
    <Button 
      type="link" 
      onClick={handleFileClick}
      style={{ padding: 0 }}
    >
      查看报告
    </Button>
  );
};

const PatientCard = ({ patient, statusMap, asMap, arMap, onAnalyze, moduleId }) => {
  // 根据模块ID返回不同的卡片内容
  const renderCardContent = () => {
    if (moduleId === 'huanzhegenjinmingxi') {
      return (
        <div className="card-content">
          <div className="data-group">
            <div className="data-item">
              <div className="label">患者</div>
              <div className="value">{String(patient.huanzhe || '-')}</div>
            </div>
            <div className="data-item">
              <div className="label">EF值</div>
              <div className="value">{String(patient.efzhi || '-')}</div>
            </div>
          </div>
          <div className="data-group">
            <div className="data-item">
              <div className="label">NYHA分级</div>
              <div className="value">{String(patient.nyhaxingongnengfenji || '-')}</div>
            </div>
            <div className="data-item">
              <div className="label">LVDd值</div>
              <div className="value">{String(patient.lvddzhi || '-')}</div>
            </div>
          </div>
          <div className="data-group">
            <div className="data-item">
              <div className="label">TAPSE值</div>
              <div className="value">{String(patient.tapsezhi || '-')}</div>
            </div>
            <div className="data-item">
              <div className="label">INTERMACS分级</div>
              <div className="value">{String(patient.intermacsfenji || '-')}</div>
            </div>
          </div>
          <div className="data-group">
            <div className="data-item">
              <div className="label">诊疗报告</div>
              <div className="value">
                {renderFileLink(patient.zhenliaobaogao)}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // 默认显示全部字段（QM模块）
    return (
      <div className="card-content">
        <div className="data-group">
          <div className="data-item">
            <div className="label">AS</div>
            <div className="value">{getMappedValue(patient.as6610, asMap)}</div>
          </div>
          <div className="data-item">
            <div className="label">超声状态</div>
            <div className="value">{getMappedValue(patient.chaoshengzhuangtai, statusMap)}</div>
          </div>
          <div className="data-item">
            <div className="label">Vmax值</div>
            <div className="value">{patient.vmaxzhi || '-'}</div>
          </div>
        </div>
        <div className="data-group">
          <div className="data-item">
            <div className="label">PGmean值</div>
            <div className="value">{patient.pgmeanzhi || '-'}</div>
          </div>
          <div className="data-item">
            <div className="label">EF值</div>
            <div className="value">{patient.efzhi || '-'}</div>
          </div>
          <div className="data-item">
            <div className="label">AR</div>
            <div className="value">{getMappedValue(patient.ar6537, arMap)}</div>
          </div>
        </div>
        <div className="data-group">
          <div className="data-item">
            <div className="label">瓣口面积</div>
            <div className="value">{patient.bankoumianji || '-'}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card className="patient-card" hoverable>
      <div className="patient-header">
        <div className="patient-name">
          {moduleId === 'huanzhegenjinmingxi' ? patient.huanzhe : patient.huanzhexingming || '-'}
        </div>
      </div>
      {renderCardContent()}
      <div className="card-footer">
        <Button 
          type="primary" 
          icon={<RobotOutlined />}
          onClick={() => onAnalyze(patient)}
          className="analyze-btn"
        >
          AI 分析
        </Button>
      </div>
    </Card>
  );
};

const AnalysisResult = ({ result, patientName, onSave }) => {
  const resultRef = useRef(null);

  const handleSaveImage = async () => {
    try {
      if (resultRef.current) {
        const canvas = await html2canvas(resultRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          logging: false
        });
        
        const link = document.createElement('a');
        link.download = `${patientName || '患者'}_诊疗分析.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      }
    } catch (error) {
      message.error('保存图片失败：' + error.message);
    }
  };

  const handleSavePDF = async () => {
    try {
      if (resultRef.current) {
        const opt = {
          margin: [10, 10],
          filename: `${patientName || '患者'}_诊疗分析.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { 
            scale: 2,
            useCORS: true,
            logging: false
          },
          jsPDF: { 
            unit: 'mm', 
            format: 'a4', 
            orientation: 'portrait' 
          }
        };

        message.loading('正在生成 PDF...', 0);
        await html2pdf().set(opt).from(resultRef.current).save();
        message.destroy();
        message.success('PDF 已保存');
      }
    } catch (error) {
      message.error('保存 PDF 失败：' + error.message);
    }
  };

  useEffect(() => {
    if (typeof onSave === 'function') {
      onSave(() => handleSaveImage);
    }
  }, []);

  const formatAnalysisResult = (text) => {
    if (!text) return [];
    
    console.log('Formatting text:', text); // 添加调试日志
    
    // 将文本按段落分割，并移除空段落
    const sections = text.split(/###\s*\d*\.?\s*/)
      .filter(Boolean)
      .map(section => section.trim())
      .filter(section => section !== '--');
    
    console.log('Sections:', sections); // 添加调试日志
    
    return sections.map((section, index) => {
      const lines = section.split('\n').filter(Boolean);
      const title = lines[0].trim();
      
      // 判断是否为开头段落
      const isIntroSection = index === 0;
      
      const content = lines.slice(1).map(line => {
        if (line.startsWith('-') || line.startsWith('•')) {
          return {
            type: 'content',
            text: line.replace(/^[-•]\s*/, '').trim()
          };
        }
        if (line.includes('：') || line.includes(':')) {
          return {
            type: 'subtitle',
            text: line.trim()
          };
        }
        return {
          type: 'content',
          text: line.trim()
        };
      }).filter(item => item.text);

      return {
        title,
        content,
        isIntroSection
      };
    });
  };

  const formattedResult = formatAnalysisResult(result);

  return (
    <div className="analysis-result-container" ref={resultRef}>
      <div className="analysis-result-header">
        <img src="/logo.png" alt="logo" className="analysis-logo" />
        <h2>库叮AI 心脏筛查分析报告</h2>
        <div className="patient-info">
          <span>患者：{patientName || '-'}</span>
          <span>分析时间：{new Date().toLocaleString()}</span>
        </div>
      </div>
      <div className="analysis-result-content">
        {formattedResult.map((section, index) => (
          <div key={index} className={`analysis-section ${section.isIntroSection ? 'intro-section' : ''}`}>
            <h3 className="section-title">
              {!section.isIntroSection && (
                <span className="section-number">{index}</span>
              )}
              {section.title}
            </h3>
            <div className="section-content">
              {section.content.map((item, i) => (
                <div 
                  key={i} 
                  className={`content-item ${item.type === 'subtitle' ? 'subtitle' : ''}`}
                >
                  {item.type === 'subtitle' ? (
                    <strong className="subtitle-text">{item.text}</strong>
                  ) : (
                    item.text
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="analysis-result-footer">
        <div className="footer-text">
          *本分析报告由 AI 辅助生成，仅供参考，具体诊疗方案请遵医嘱。
        </div>
      </div>
    </div>
  );
};

const DiagnosisAssistant = ({ moduleId = 'huanzhegenjinmingxi' }) => {
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [statusMap, setStatusMap] = useState({});
  const [asMap, setAsMap] = useState({});
  const [arMap, setArMap] = useState({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0
  });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState('');
  const [analysisModalVisible, setAnalysisModalVisible] = useState(false);
  const [currentPatient, setCurrentPatient] = useState(null);
  const [saveImageFn, setSaveImageFn] = useState(() => () => {});
  const [savePDFFn, setSavePDFFn] = useState(() => () => {});
  const [isIndicatorModalVisible, setIsIndicatorModalVisible] = useState(false);
  const navigate = useNavigate();

  const indicatorDescriptions = {
    as: {
      title: '主动脉瓣狭窄（AS）',
      desc: '主动脉瓣狭窄是指主动脉瓣口变窄，阻碍左心室血液流向主动脉的一种疾病。严重程度通常分为轻度、中度和重度。'
    },
    status: {
      title: '超声状态',
      desc: '超声检查的质量状态，影响检查结果的准确性和可靠性。良好的超声窗对准确评估心脏功能至关重要。'
    },
    vmax: {
      title: 'Vmax值（最大血流速度）',
      desc: '通过主动脉瓣的最大血流速度，正常值<2.0m/s。速度升高提示主动脉瓣狭窄：\n- 轻度：2.0-2.9m/s\n- 中度：3.0-3.9m/s\n- 重度：≥4.0m/s'
    },
    pgmean: {
      title: 'PGmean值（平均压力阶差）',
      desc: '主动脉瓣膜两侧的平均压力差，正常值<20mmHg。压差增加提示狭窄程度：\n- 轻度：<20mmHg\n- 中度：20-40mmHg\n- 重度：>40mmHg'
    },
    ef: {
      title: 'EF值（射血分数）',
      desc: '左心室每次收缩时射出血液量占舒张末期容量的百分比，反映心脏收缩功能。正常值>55%：\n- 轻度减低：45-54%\n- 中度减低：30-44%\n- 重度减低：<30%'
    },
    ar: {
      title: '主动脉瓣反流（AR）',
      desc: '主动脉瓣在舒张期关闭不全导致血液反流入左心室的情况。程度分为：\n- 轻度：反流量小，对心功能影响轻微\n- 中度：反流量中等，需要定期随访\n- 重度：反流量大，可能需要手术干预'
    },
    area: {
      title: '瓣口面积',
      desc: '主动脉瓣口的实际开放面积，正常值>2.0cm²。狭窄程度评估：\n- 轻度：>1.5cm²\n- 中度：1.0-1.5cm²\n- 重度：<1.0cm²'
    },
    nyha: {
      title: 'NYHA心功能分级',
      desc: 'NYHA分级是评估心力衰竭患者症状严重程度的标准方法：\n- I级：日常活动不受限\n- II级：轻度活动受限\n- III级：明显活动受限\n- IV级：静息状态下也有症状'
    },
    lvdd: {
      title: 'LVDd值（左室舒张末期内径）',
      desc: '左心室在舒张末期的内径，是评估左心室大小的重要指标。正常值范围因性别和体表面积而异，一般在35-56mm之间。'
    },
    tapse: {
      title: 'TAPSE值（三尖瓣环收缩期位移）',
      desc: '反映右心室收缩功能的重要指标。正常值≥17mm，<17mm提示右心功能减低。'
    },
    intermacs: {
      title: 'INTERMACS分级',
      desc: '评估晚期心力衰竭患者的临床状态：\n- 1级：心源性休克\n- 2级：进行性恶化\n- 3级：稳定但依赖药物\n- 4级：休息时症状\n- 5-7级：活动耐量不同程度受限'
    }
  };

  // 根据模块ID获取对应的字段配置
  const getFieldConfig = () => {
    switch (moduleId) {
      case 'huanzhegenjinmingxi':
        return {
          nameField: 'huanzhe',
          idField: 'id',
          fields: [
            'id',
            'huanzhe',
            'efzhi',
            'nyhaxingongnengfenji',
            'lvddzhi',
            'tapsezhi',
            'intermacsfenji',
            'zhenliaobaogao'
          ]
        };
      case 'qmhuanzhexinxi':
      default:
        return {
          nameField: 'huanzhexingming',
          idField: 'qmhuanzhexinxiNo',
          fields: [
            'qmhuanzhexinxiNo',
            'huanzhexingming',
            'as6610',
            'chaoshengzhuangtai',
            'vmaxzhi',
            'pgmeanzhi',
            'efzhi',
            'ar6537',
            'bankoumianji'
          ]
        };
    }
  };

  const fieldConfig = getFieldConfig();

  // 修改 processValue 函数
  const processValue = (value) => {
    if (!value) return value;

    // 如果是字符串且以 rb/ 开头，直接返回字符串
    if (typeof value === 'string' && value.startsWith('rb/')) {
      return value;
    }

    // 处理对象类型
    if (typeof value === 'object' && value !== null) {
      if (value.type === 'FILE' || value.fileType === 'FILE' || value.contentType) {
        return value.url || value.downloadUrl || value.fileUrl || value.toString();
      }
      return value.text || value.toString();
    }

    return value;
  };

  const fetchPatients = async (params = {}) => {
    try {
      setLoading(true);
      const { current, pageSize } = params;
      const response = await getEntityData(moduleId, {
        page: current,
        pageSize,
        searchText: searchText,
        fields: fieldConfig.fields.join(','),
        searchFields: moduleId === 'huanzhegenjinmingxi' ? 
          'huanzhe' : 'huanzhexingming'
      });

      if (response && response.list) {
        console.log('Raw patient data:', response.list);
        
        setPatients(response.list.map(item => ({
          ...item,
          id: item[fieldConfig.idField],
          huanzhe: processValue(item.huanzhe),
          huanzhexingming: processValue(item[fieldConfig.nameField]),
          efzhi: processValue(item.efzhi),
          as6610: processValue(item.as6610),
          chaoshengzhuangtai: processValue(item.chaoshengzhuangtai),
          vmaxzhi: processValue(item.vmaxzhi),
          pgmeanzhi: processValue(item.pgmeanzhi),
          ar6537: processValue(item.ar6537),
          bankoumianji: processValue(item.bankoumianji),
          nyhaxingongnengfenji: processValue(item.nyhaxingongnengfenji),
          lvddzhi: processValue(item.lvddzhi),
          tapsezhi: processValue(item.tapsezhi),
          intermacsfenji: processValue(item.intermacsfenji),
          zhenliaobaogao: processValue(item.zhenliaobaogao)
        })));
        setPagination(prev => ({
          ...prev,
          total: response.total || 0
        }));
      }
    } catch (error) {
      console.error('获取患者列表失败:', error);
      message.error('获取患者列表失败：' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchMappingData = async () => {
    try {
      // 只有 QM 模块需要获取映射数据
      if (moduleId === 'qmhuanzhexinxi') {
        const [asData, statusData, arData] = await Promise.all([
          getPicklistData(moduleId, 'as6610'),
          getPicklistData(moduleId, 'chaoshengzhuangtai'),
          getPicklistData(moduleId, 'ar6537')
        ]);

        const processMapping = (data) => {
          const mapping = {};
          if (Array.isArray(data)) {
            data.forEach(item => {
              mapping[item.id] = { text: item.text };
            });
          }
          return mapping;
        };

        setAsMap(processMapping(asData));
        setStatusMap(processMapping(statusData));
        setArMap(processMapping(arData));
      } else {
        // HX 模块不需要映射数据，设置为空对象
        setAsMap({});
        setStatusMap({});
        setArMap({});
      }
    } catch (error) {
      console.error('获取映射数据失败:', error);
      if (moduleId === 'qmhuanzhexinxi') {
        message.error('获取映射数据失败');
      }
    }
  };

  const handleAnalyze = async (patient) => {
    try {
      setIsAnalyzing(true);
      setAnalysisModalVisible(true);
      setCurrentPatient(patient);

      // 构建诊疗报告图片的完整URL
      const reportImageUrl = patient.zhenliaobaogao ? 
        `${FILE_SERVER_URL}/${patient.zhenliaobaogao}` : null;

      // 根据模块构建不同的提示语
      const prompt = moduleId === 'huanzhegenjinmingxi'
        ? `作为一名经验丰富的心血管外科专家，请根据以下检查指标和诊疗报告进行分析并给出诊疗建议：

### 1. 病情评估
患者检查指标如下：
- EF值（射血分数）：${patient.efzhi}%
- NYHA心功能分级：${patient.nyhaxingongnengfenji || '-'}
- LVDd值：${patient.lvddzhi || '-'}
- TAPSE值：${patient.tapsezhi || '-'}
- INTERMACS分级：${patient.intermacsfenji || '-'}

### 2. 诊疗报告分析
${reportImageUrl ? `
请根据患者的诊疗报告图片（${reportImageUrl}）进行详细分析：
- 报告中的关键检查指标
- 各项指标的异常情况
- 心脏结构和功能评估
- 血流动力学特征
- 其他重要发现
` : '未提供诊疗报告'}

### 3. 综合风险评估
- 基于检查指标的风险分析
- 结合诊疗报告的风险评估
- 并发症风险预测
- 预后影响因素分析

### 4. 治疗建议
- 药物治疗方案
- 手术干预指征
- 康复治疗计划
- 定期复查建议

### 5. 预后预测
- 短期预后评估
- 中长期预后预测
- 生存质量分析
- 预后影响因素

### 6. 生活建议`
        : `作为一名经验丰富的心血管外科专家，请根据以下检查指标进行分析并给出诊疗建议：

### 1. 病情评估
患者检查指标如下：
- 主动脉瓣狭窄（AS）：${getMappedValue(patient.as6610, asMap)}
- Vmax值（最大血流速度）：${patient.vmaxzhi} m/s
- PGmean值（平均压力阶差）：${patient.pgmeanzhi} mmHg
- 瓣口面积：${patient.bankoumianji} mm²
- EF值（射血分数）：${patient.efzhi}%
- 主动脉瓣反流（AR）：${getMappedValue(patient.ar6537, arMap)}
- 超声状态：${getMappedValue(patient.chaoshengzhuangtai, statusMap)}

### 2. 风险分析

### 3. 治疗建议

### 4. 预后预测

### 5. 生活建议`;

      const response = await getAIAnalysis(prompt);
      if (!response) {
        throw new Error('AI 分析返回为空');
      }
      setAnalysisResult(response);
    } catch (error) {
      console.error('AI Analysis Error:', error);
      message.error('AI 分析失败：' + (error.message || '未知错误'));
      setAnalysisModalVisible(false);
    } finally {
      setIsAnalyzing(false);
    }
  };

  useEffect(() => {
    fetchPatients({ current: 1 });
    fetchMappingData();
  }, [moduleId]);

  useEffect(() => {
    // 检查是否有 API 密钥
    const appId = localStorage.getItem('appId');
    const appSecret = localStorage.getItem('appSecret');
    
    if (!appId || !appSecret) {
      message.error('请先登录');
      navigate('/login');
      return;
    }
  }, [navigate]);

  const handleSearch = () => {
    setPagination(prev => ({
      ...prev,
      current: 1  // 搜索时重置到第一页
    }));
    fetchPatients({ current: 1, pageSize: pagination.pageSize });
  };

  const handleReset = () => {
    setSearchText('');
    setPagination(prev => ({
      ...prev,
      current: 1  // 重置时回到第一页
    }));
    fetchPatients({ current: 1, pageSize: pagination.pageSize });
  };

  const handleSaveImage = () => {
    if (typeof saveImageFn === 'function') {
      saveImageFn();
    }
  };

  const handleSavePDF = () => {
    if (typeof savePDFFn === 'function') {
      savePDFFn();
    }
  };

  // 修改渲染内容部分，将卡片式布局改为表格列表
  const columns = [
    {
      title: '患者',
      dataIndex: 'huanzhe',
      key: 'huanzhe',
      width: 150,
    },
    {
      title: 'EF值',
      dataIndex: 'efzhi',
      key: 'efzhi',
      width: 100,
    },
    {
      title: 'NYHA分级',
      dataIndex: 'nyhaxingongnengfenji',
      key: 'nyhaxingongnengfenji',
      width: 120,
    },
    {
      title: 'LVDd值',
      dataIndex: 'lvddzhi',
      key: 'lvddzhi',
      width: 100,
    },
    {
      title: 'TAPSE值',
      dataIndex: 'tapsezhi',
      key: 'tapsezhi',
      width: 100,
    },
    {
      title: 'INTERMACS分级',
      dataIndex: 'intermacsfenji',
      key: 'intermacsfenji',
      width: 150,
    },
    {
      title: '诊疗报告',
      dataIndex: 'zhenliaobaogao',
      key: 'zhenliaobaogao',
      width: 150,
      render: (text) => renderFileLink(text)
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            onClick={() => handleAnalyze(record)}
          >
            分析
          </Button>
        </Space>
      ),
    }
  ];

  // 修改 fetchPatients 函数的调用方式
  const handlePageChange = (page, pageSize) => {
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize: pageSize
    }));
    fetchPatients({ current: page, pageSize: pageSize });
  };

  // 添加空状态展示
  const customEmpty = () => (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <span>暂无数据，请确保已正确登录并输入有效的 API 密钥</span>
      }
    />
  );

  return (
    <div className="diagnosis-assistant">
      <Card
        extra={
          <Tooltip title="查看指标说明">
            <Button
              type="link"
              icon={<QuestionCircleOutlined />}
              onClick={() => setIsIndicatorModalVisible(true)}
            >
              指标说明
            </Button>
          </Tooltip>
        }
      >
        <Table
          columns={columns}
          dataSource={patients}
          rowKey={fieldConfig.idField}
          loading={loading}
          locale={{ emptyText: customEmpty() }}
          pagination={{
            total: pagination.total,
            current: pagination.current,
            pageSize: pagination.pageSize,
            onChange: handlePageChange,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`
          }}
          scroll={{ x: 'max-content' }}
        />

        <Modal
          title="AI 分析结果"
          open={analysisModalVisible}
          onCancel={() => setAnalysisModalVisible(false)}
          footer={[
            <Button
              key="save-image"
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleSaveImage}
              disabled={isAnalyzing}
            >
              保存为图片
            </Button>,
            <Button
              key="save-pdf"
              type="primary"
              icon={<FilePdfOutlined />}
              onClick={handleSavePDF}
              disabled={isAnalyzing}
            >
              保存为PDF
            </Button>,
            <Button key="close" onClick={() => setAnalysisModalVisible(false)}>
              关闭
            </Button>
          ]}
          width={800}
        >
          {isAnalyzing ? (
            <div className="analysis-loading">
              <Space direction="vertical" align="center">
                <RobotOutlined spin />
                <div>AI 正在分析中...</div>
              </Space>
            </div>
          ) : (
            <AnalysisResult 
              result={analysisResult} 
              patientName={currentPatient?.huanzhexingming}
              onSave={setSavePDFFn}
            />
          )}
        </Modal>

        <Modal
          title="指标说明"
          open={isIndicatorModalVisible}
          onCancel={() => setIsIndicatorModalVisible(false)}
          footer={[
            <Button key="close" type="primary" onClick={() => setIsIndicatorModalVisible(false)}>
              关闭
            </Button>
          ]}
          width={600}
        >
          <div className="indicator-descriptions">
            {Object.values(indicatorDescriptions).map(({ title, desc }) => (
              <div key={title} className="indicator-item">
                <h3>{title}</h3>
                <p>{desc.split('\n').map((line, i) => (
                  <span key={i}>
                    {line}
                    <br />
                  </span>
                ))}</p>
              </div>
            ))}
          </div>
        </Modal>
      </Card>
    </div>
  );
};

export default DiagnosisAssistant; 